import {
  Component,
  createSignal,
  createEffect,
  createMemo,
  onCleanup,
  untrack,
  Show,
  For,
} from 'solid-js';
import { translate } from '@fulcrum/lib';
import { getLogger } from '@trimble/logging';
import {
  IExportJobDetail,
  useApiServices,
} from '@trimble/tc-issue-management-providers';
import type { ITrackedExportJob } from '../../types/issue.types';
// eslint-disable-next-line @nx/enforce-module-boundaries -- ProgressWidget is required as the base UI component for the widget structure
import { ProgressWidget } from '@tcweb/connect-ui';
import { ExchangeJobStatus } from './ExchangeJobStatus';
import { ExchangeJobStatusIcon } from './ExchangeJobStatusIcon';
import './IssueExchangeWidget.css';
import {
  IssueExchangeStatus,
  IssueManagementWidgetMode,
} from '../../types/issue.types';

export interface IssueExchangeWidgetProps {
  /** Export jobs to display */
  exportJobs: ITrackedExportJob[];
  /** Widget mode */
  mode?: IssueManagementWidgetMode;
  /** Project ID for API calls */
  projectId: string;
  /** Whether the app is embedded */
  isEmbedded?: boolean;
  /** Current export batch number */
  currentExportBatchNumber?: number;
  /** Callback to update a job */
  onUpdateJob?(job: IExportJobDetail): void;
  /** Callback to clear all exports */
  onClearExports?(): void;
}

const logger = getLogger();

export const IssueExchangeWidget: Component<IssueExchangeWidgetProps> = (
  props
) => {
  const apiServices = useApiServices();
  const POLL_INTERVAL = 3000;

  const [isProcessing, setIsProcessing] = createSignal(false);
  const [isCollapsed, setIsCollapsed] = createSignal(false);
  const [processedItems, setProcessedItems] = createSignal<IExportJobDetail[]>(
    []
  );
  const [failedItems, setFailedItems] = createSignal<IExportJobDetail[]>([]);
  const [errorInfo, setErrorInfo] = createSignal<Record<string, string>>({});
  const [prevJobsLength, setPrevJobsLength] = createSignal(0);
  const [prevBatchNumber, setPrevBatchNumber] = createSignal(0);
  const [localJobStatusMap, setLocalJobStatusMap] = createSignal<
    Record<string, IExportJobDetail>
  >({});

  let timer: ReturnType<typeof setInterval> | undefined;
  // Incremented each time a new batch starts. In-flight getJobs calls check
  // this before writing state so stale responses from superseded batches are
  // discarded rather than corrupting the current batch.
  let currentGeneration = 0;

  // Create a memo to track export jobs reactively (must be declared before createEffect)
  const exportJobsMemo = createMemo(() => props.exportJobs);

  onCleanup(() => {
    if (timer) {
      clearInterval(timer);
    }
  });

  createEffect(() => {
    const currentJobs = exportJobsMemo();
    const currentJobsLength = currentJobs.length;
    const currentBatchNumber = props.currentExportBatchNumber || 0;

    // Read the "previous" sentinels without tracking them as reactive
    // dependencies. They are edge-detection state, not triggers — the effect
    // should only re-run when the *input* (jobs list or batch number) changes,
    // not when onClose resets the sentinels.
    const prevLength = untrack(prevJobsLength);
    const prevBatch = untrack(prevBatchNumber);

    if (currentJobsLength > prevLength && currentBatchNumber !== prevBatch) {
      const newItems = currentJobs.filter(
        (item) =>
          item.status !== IssueExchangeStatus.Completed &&
          item.status !== IssueExchangeStatus.Failed
      );
      if (newItems.length > 0) {
        processJobs(newItems);
      }
      setPrevJobsLength(currentJobsLength);
      setPrevBatchNumber(currentBatchNumber);
    }
  });

  const getJobs = async (
    jobs: IExportJobDetail[],
    generation: number
  ): Promise<void> => {
    if (!apiServices || generation !== currentGeneration) {
      return;
    }

    const promises = jobs.map(async (job) => {
      try {
        const detail = await apiServices.issueExportApi.getExportJobDetail(
          job.projectId,
          job.jobId
        );
        return { originalJob: job, detail };
      } catch (error) {
        logger.error(
          `[IssueExchangeWidget] Failed to get job detail for ${job.jobId}:`,
          error
        );
        return { originalJob: job, detail: null };
      }
    });

    const results = await Promise.all(promises);

    // Discard results if this batch was superseded while awaiting.
    if (generation !== currentGeneration) {
      return;
    }

    results.forEach(({ originalJob, detail: jobDetail }) => {
      if (jobDetail) {
        const trackedDetail = { ...jobDetail, jobId: originalJob.jobId };

        props.onUpdateJob?.(trackedDetail);

        setLocalJobStatusMap((prev) => ({
          ...prev,
          [originalJob.jobId]: trackedDetail,
        }));

        if (jobDetail.status === IssueExchangeStatus.Completed) {
          setProcessedItems((prev) => {
            if (!prev.some((item) => item.jobId === originalJob.jobId)) {
              return [...prev, trackedDetail];
            }
            return prev;
          });
        } else if (jobDetail.status === IssueExchangeStatus.Failed) {
          setFailedItems((prev) => {
            if (!prev.some((item) => item.jobId === originalJob.jobId)) {
              return [...prev, trackedDetail];
            }
            return prev;
          });
          if (jobDetail.error) {
            setErrorInfo((prev) => ({
              ...prev,
              [originalJob.jobId]: jobDetail.error?.message || 'Export failed',
            }));
          }
        }
      }
    });

    // Stop polling when every job in this batch has reached a terminal state.
    // Checking against the closed-over `jobs` array (not a global counter)
    // means retries never inflate the threshold beyond what can be reached.
    const statusMap = localJobStatusMap();
    const allDone = jobs.every((job) => {
      const status = statusMap[job.jobId]?.status;
      return (
        status === IssueExchangeStatus.Completed ||
        status === IssueExchangeStatus.Failed
      );
    });

    if (allDone) {
      if (timer) {
        clearInterval(timer);
      }
      setIsProcessing(false);
    }
  };

  const processJobs = (jobs: IExportJobDetail[], isRetry = false): void => {
    const generation = ++currentGeneration;

    const processedJobs = jobs.filter(
      (job) => job.status !== IssueExchangeStatus.Completed
    );

    setIsProcessing(true);
    setIsCollapsed(false);

    if (!isRetry) {
      setProcessedItems([]);
      setFailedItems([]);
      setErrorInfo({});
      setLocalJobStatusMap({});
    }

    if (timer) {
      clearInterval(timer);
    }

    timer = setInterval(() => {
      if (generation === currentGeneration) {
        getJobs(processedJobs, generation).catch((error) => {
          logger.error('[IssueExchangeWidget] getJobs polling failed:', error);
        });
      }
    }, POLL_INTERVAL);

    getJobs(processedJobs, generation).catch((error) => {
      logger.error(
        '[IssueExchangeWidget] getJobs initial fetch failed:',
        error
      );
    });
  };

  const resetErrorInfo = (item: IExportJobDetail): void => {
    setErrorInfo((prev) => {
      const newInfo = { ...prev };
      delete newInfo[item.jobId];
      return newInfo;
    });
  };

  const onRetry = (item: IExportJobDetail): void => {
    const updatedJob: IExportJobDetail = {
      ...item,
      status: IssueExchangeStatus.Processing,
      progress: {
        ...item.progress,
        percentage: 0,
      },
    };
    props.onUpdateJob?.(updatedJob);

    setFailedItems((prev) =>
      prev.filter((failedItem) => failedItem.jobId !== item.jobId)
    );
    resetErrorInfo(item);
    processJobs([updatedJob], true);
  };

  const onRetryAll = (): void => {
    const updatedJobs = failedItems().map((item) => {
      const updatedJob: IExportJobDetail = {
        ...item,
        status: IssueExchangeStatus.Processing,
        progress: {
          ...item.progress,
          percentage: 0,
        },
      };
      props.onUpdateJob?.(updatedJob);
      resetErrorInfo(item);
      return updatedJob;
    });

    setFailedItems([]);
    processJobs(updatedJobs, true);
  };

  const onClose = (): void => {
    // Invalidate any in-flight getJobs calls so their results are discarded.
    currentGeneration++;
    if (timer) {
      clearInterval(timer);
    }
    setFailedItems([]);
    setProcessedItems([]);
    setLocalJobStatusMap({});
    setIsProcessing(false);
    setPrevJobsLength(0);
    setPrevBatchNumber(0);
    props.onClearExports?.();
  };

  // Use Show component for conditional rendering instead of early return
  const totalCount = createMemo(() => exportJobsMemo().length);
  const topicExportTitle = translate('TC.Topic.ExportingTopics');
  const exportProcessingFooterMsg = createMemo(
    () =>
      `${translate('TCS.Mapviewer.Filters.Exported')} ${processedItems().length} ${translate('TC.Common.Of')} ${totalCount()}`
  );

  const uploadSuccessTitle = translate('TC.File.Upload.CompletedMessage');
  const uploadFailureTitle = createMemo(() =>
    totalCount() === 1
      ? translate('TC.File.Upload.FailedTitleMessage')
      : translate('TC.File.Upload.ErrorTitleMessage')
          .replace(
            '{{successCount}}',
            (totalCount() - failedItems().length).toString()
          )
          .replace('{{totalCount}}', totalCount().toString())
  );
  const uploadFailureMsg = createMemo(() =>
    translate(
      totalCount() === 1
        ? 'TC.File.Upload.SingleErrorDescriptionMessage'
        : 'TC.File.Upload.ErrorDescriptionMessage'
    ).replace('{{errorCount}}', failedItems().length.toString())
  );

  // Convert imperative logic to reactive memos (similar to React render logic)
  const headerIcon = createMemo(() => {
    if (props.mode === IssueManagementWidgetMode.IssueExport) {
      return 'tc-icon-content-export';
    }
    if (isProcessing()) {
      return '';
    }
    if (failedItems().length > 0) {
      return 'tc-icon-info-outline';
    }
    return 'tc-icon-check-circle';
  });

  const title = createMemo(() => {
    if (props.mode === IssueManagementWidgetMode.IssueExport) {
      return topicExportTitle;
    }
    if (isProcessing()) {
      return '';
    }
    if (failedItems().length > 0) {
      return uploadFailureTitle();
    }
    return uploadSuccessTitle;
  });

  const footerMessage = createMemo((): string | undefined => {
    if (isProcessing()) {
      return props.mode === IssueManagementWidgetMode.IssueExport
        ? exportProcessingFooterMsg()
        : undefined;
    }
    if (failedItems().length > 0) {
      return uploadFailureMsg();
    }
    return undefined;
  });

  const PERCENTAGE_MULTIPLIER = 100;
  const DECIMAL_PLACES = 2;

  const progress = createMemo(() => {
    if (props.mode === IssueManagementWidgetMode.Upload) {
      return `${((processedItems().length * PERCENTAGE_MULTIPLIER) / totalCount()).toFixed(DECIMAL_PLACES)}%`;
    }
    const jobs = exportJobsMemo();
    if (jobs.length === 0) return '0%';
    const totalPercentage = jobs.reduce(
      (sum, job) => sum + (job.progress?.percentage ?? 0),
      0
    );
    return `${Math.round(totalPercentage / jobs.length)}%`;
  });

  const showProgressBar = createMemo(() => {
    return isProcessing() || failedItems().length > 0;
  });

  const asideSection = createMemo(() => {
    if (isProcessing()) {
      return undefined;
    }
    if (failedItems().length > 0) {
      return (
        <aside class="px-2 row-center row-distribute py-1">
          <a class="ism-small ism-pointer" onClick={onRetryAll}>
            {translate('TC.File.Upload.RetryAll')}
          </a>
        </aside>
      );
    }
    return undefined;
  });

  const isFailure = createMemo(() => failedItems().length > 0);

  // Remove date/time from job name
  const stripDateTimeFromName = (name: string): string => {
    // Pattern 1: Date with slashes/dashes followed by time (MM/DD/YYYY or DD/MM/YYYY)
    let cleaned = name.replace(/\s+\d{1,2}[/-]\d{1,2}[/-]\d{2,4}.*$/i, '');

    // Pattern 2: ISO date format (YYYY-MM-DD) followed by time
    cleaned = cleaned.replace(/\s+\d{4}[/-]\d{1,2}[/-]\d{1,2}.*$/i, '');
    if (/^Export\s+/i.test(cleaned)) {
      const match = cleaned.match(/^Export\s+(.+)$/i);
      if (match && /\d/.test(match[1])) {
        cleaned = 'Export';
      }
    }

    return cleaned.trim();
  };

  const computedTitle = createMemo(() => {
    const baseTitle = title();
    if (isCollapsed() && isFailure() && footerMessage()) {
      return `${baseTitle} ${footerMessage()}`;
    }
    return baseTitle;
  });

  // Build the content for ProgressWidget
  const widgetContent = createMemo(() => (
    <>
      <Show when={asideSection()}>{asideSection()}</Show>

      {/* eslint-disable-next-line @typescript-eslint/naming-convention */}
      <div class="overflowY" style={{ 'max-height': '20rem' }}>
        <ul>
          <For each={exportJobsMemo()}>
            {(item) => {
              const effectiveItem = (): IExportJobDetail =>
                localJobStatusMap()[item.jobId] ?? item;
              return (
                <li class="py-1 pl-2 pr-1 row-center row-distribute">
                  <div class="ism-flex-1 pr-1 m-width-0">
                    <div class="flex-row row-distribute ism-flex-1">
                      <div class="ism-flex-1 text-ellipsis">
                        <p class="text-ellipsis">
                          {stripDateTimeFromName(item.name)}
                        </p>
                        <div class="ism-small text-ellipsis">
                          <ExchangeJobStatus
                            item={effectiveItem()}
                            errorMessage={errorInfo()[item.jobId] || ''}
                          />
                        </div>
                      </div>
                    </div>

                    <div role="progressbar" class="progressbar">
                      <span
                        style={{
                          width: effectiveItem().progress
                            ? `${effectiveItem().progress.percentage}%`
                            : '0%',
                        }}
                      />
                    </div>
                  </div>
                  <div class="flex-row">
                    <ExchangeJobStatusIcon
                      status={effectiveItem().status as IssueExchangeStatus}
                      onRetry={() => onRetry(effectiveItem())}
                    />
                  </div>
                </li>
              );
            }}
          </For>
        </ul>
      </div>
      <Show when={!isCollapsed() && footerMessage()}>
        <div class="ism-small text-right pt-1 px-2">
          <span class="text-meta">{footerMessage()}</span>
        </div>
      </Show>
    </>
  ));

  // Build widget class string
  const widgetClass = createMemo(() => {
    const classes = ['upload'];
    if (isFailure()) {
      classes.push('error');
    }
    return classes.join(' ');
  });

  return (
    <Show when={totalCount() > 0}>
      <div
        class="ism-exchange-widget toast-container position-bottom-right"
        data-cy="issues-export"
      >
        <div class="progress-widget-wrapper">
          {/* ProgressWidget with close button wrapper */}
          <div class="progress-widget-with-close">
            <ProgressWidget
              title={computedTitle()}
              icon={headerIcon()}
              progressPercent={progress()}
              content={widgetContent()}
              collapsed={isCollapsed()}
              onCollapseChange={setIsCollapsed}
              showProgress={showProgressBar()}
              class={widgetClass()}
            />
            {/* Close button - positioned absolutely */}
            <button
              class="ism-button ism-default hollow icon-medium icon-circle close-button"
              onClick={onClose}
              aria-label="Close"
            >
              <i class="icon-font tc-icon-content-close" />
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
};
